import { Aside } from "./components/Aside";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { cx } from "shared/utils";
import { MobileNavBar } from "providers/Layout/components/MobileNavBar";
import { Hide, Show } from "shared/components";
import { useMedia } from "shared/providers";

export interface Props {
  header?: JSX.Element;
  sidebar?: JSX.Element;
  headerClassName?: string;
  footer?: ReactNode;
  footerClassName?: string;
  sectionClassName?: string;
  layoutClassName?: string;
  children: ReactNode;
  // For scrolling purposes
  sectionIdentifier?: string;
  hideFooterForDesktop?: boolean;
  displayAside?: boolean;
  hideHeaderOnScroll?: boolean;
}

export const Layout: FC<Props> = ({
  footer,
  sidebar,
  header,
  sectionClassName,
  footerClassName,
  headerClassName,
  layoutClassName,
  children,
  sectionIdentifier,
  hideFooterForDesktop,
  displayAside = true,
  hideHeaderOnScroll
}) => {
  const [isOpenFromMobile, setIsOpenFromMobile] = useState(false);
  const headerRef = useRef<HTMLHeadElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);

  const { pathname } = useLocation();
  const { isMobile } = useMedia();

  const isDefaultCollapsedPage =
    pathname === "/applications" ||
    pathname.startsWith("/verification-templates/") ||
    pathname.startsWith("/applications/");

  useEffect(() => {
    const observeResize = () => {
      const observer = new ResizeObserver(([entry]) => {
        setHeaderHeight(entry.contentRect.height);
      });

      if (headerRef.current) observer.observe(headerRef.current);

      return () => observer.disconnect();
    };

    return observeResize();
  }, []);

  return (
    <div
      className={cx(
        "flex flex-col h-screen overflow-y-hidden print:overflow-y-visible",
        layoutClassName
      )}
    >
      <MobileNavBar setIsOpenFromMobile={setIsOpenFromMobile} />

      {/* Backdrop for mobile aside */}
      <div
        onClick={
          isOpenFromMobile ? () => setIsOpenFromMobile(false) : undefined
        }
        className={cx(
          "fixed inset-0 z-[13] bg-alpha-400 transition-all",
          isOpenFromMobile ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      />
      <div className="flex w-full h-full">
        <Show if={displayAside}>
          <Aside
            isDefaultCollapsedPage={isDefaultCollapsedPage}
            isOpenFromMobile={isOpenFromMobile}
            setIsOpenFromMobile={setIsOpenFromMobile}
          />
        </Show>
        <main
          className={cx(
            "max-h-screen flex-grow md:pl-20 print:!pl-0 md:flex md:flex-row block pt-16 md:pt-0 overscroll-y-none",
            isDefaultCollapsedPage && displayAside ? "" : "lg:pl-0"
          )}
        >
          <Show if={sidebar}>
            <div className="w-auto max-w-full border-r border-alpha-100 bg-white">
              {sidebar}
            </div>
          </Show>
          <div className="relative w-full flex flex-col h-full">
            <Show if={header}>
              <header
                ref={headerRef}
                className={cx(
                  headerClassName ||
                    "border-b print:border-none border-alpha-100 bg-white py-3",
                  hideHeaderOnScroll
                    ? `transition-transform duration-200 absolute ${
                        headerVisible ? "translate-y-0" : "-translate-y-full"
                      }`
                    : ""
                )}
              >
                {header}
              </header>
            </Show>
            <section
              onScroll={(e) =>
                hideHeaderOnScroll &&
                setHeaderVisible(e.currentTarget.scrollTop === 0)
              }
              className={cx(
                "flex-grow hide-scroll overflow-y-auto bg-background-900 print:bg-white p-3 md:p-6 print:!h-auto print:overflow-y-visible print:p-0",
                sectionClassName
              )}
              style={{
                paddingTop: hideHeaderOnScroll ? headerHeight : undefined
              }}
              id={sectionIdentifier}
            >
              {children}
            </section>
            <Show if={footer}>
              <Hide if={hideFooterForDesktop && !isMobile}>
                <footer
                  className={
                    footerClassName ||
                    "border-t border-alpha-100 bg-white print:border-none md:px-6 py-3"
                  }
                >
                  {footer}
                </footer>
              </Hide>
            </Show>
          </div>
        </main>
      </div>
    </div>
  );
};
